import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { setUrlId } from '@/lib/url-utils';
import { restoredPages } from './usePersistedPath';

// Module-level: survives SPA navigation, cleared on full page reload.
// This is the key distinction from sessionStorage — editing the URL bar
// triggers a full reload which resets this, so stale IDs aren't restored.
const savedIds: Record<string, string | undefined> = {};

/** Clears all saved deep-link IDs. Called on logout. */
export const clearSavedIds = () => {
  for (const key of Object.keys(savedIds)) delete savedIds[key];
};

/**
 * Preserves `?id=` across SPA page switches (e.g., schedules → playlists → back).
 *
 * On every `id` change, saves to a module-level variable.
 * On mount (SPA navigation back), restores `?id=` if the URL doesn't already have one.
 * Full page reloads clear module-level state, so manually clearing `?id=` works correctly.
 *
 * Saves happen in an effect body (not cleanup), so unmount during logout
 * can't re-populate state after fullLogout's clearSavedIds().
 *
 * @returns `true` while a saved `?id=` is still on its way back into the URL. A page that
 *   auto-selects a row must skip while this is set, or it picks a DIFFERENT row first and the panel
 *   paints that one before the restore lands — visible whenever the saved id is not the list's first
 *   row, and made worse by the idle prefetch, which has the first row's detail warm and ready to
 *   paint instantly (#2151).
 *
 *   Seeded SYNCHRONOUSLY, during the first render. The restore itself happens in the mount effect
 *   below and its `setSearchParams` lands a commit later still, so anything derived from an effect
 *   would be too late to close the window it is meant to close.
 */
export function useDeepLinkRestore(pageKey: string): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('id') ?? undefined;
  const idRef = useRef(id);
  idRef.current = id;

  // Read on the first render, before the mount effect below consumes savedIds.
  const [restoreQueued] = useState(() => !id && Boolean(savedIds[pageKey]));

  // One-shot latch, same rule as `usePersistedPath`. `restoreQueued` is seeded on the FIRST render,
  // but `!id` in the return below is re-read on EVERY render — so without this the flag re-arms
  // every time a later navigation drops `?id=` from the URL, long after the restore landed. The
  // schedules sidebar does exactly that on Trash (`setSearchParams({ path: 'trash' })`) and on Home
  // with nothing selected (`setSearchParams({})`), and the consumer's auto-select then stayed
  // suppressed for the rest of the mount (#2476).
  const restoreLandedRef = useRef(false);
  if (id) restoreLandedRef.current = true;

  // On mount: restore saved id if URL doesn't have one
  useEffect(() => {
    // If usePersistedPath already restored params (including id) for this page,
    // skip to avoid a competing setSearchParams call that would overwrite the path.
    if (restoredPages.has(pageKey)) {
      // usePersistedPath restored the params, id included — still a restore in flight, so leave
      // `restoreQueued` alone and let the arriving id clear it below.
      restoredPages.delete(pageKey);
      delete savedIds[pageKey];
    } else if (!idRef.current && savedIds[pageKey]) {
      const idToRestore = savedIds[pageKey] ?? '';
      delete savedIds[pageKey];
      setUrlId(setSearchParams, idToRestore);
    } else {
      delete savedIds[pageKey];
      // Deliberately does NOT clear `restoreQueued`. Under StrictMode this effect runs twice, and
      // the first run has already consumed savedIds — so the second run lands here even when a
      // restore IS in flight, and releasing would re-open the window a commit before the id
      // arrives. (`usePersistedPath` dodges the same double-invoke by deferring its own delete,
      // see #892.) Clearing is unnecessary anyway: `restoreQueued` is only ever true when this
      // mount had a saved id, and every branch that sees one delivers it.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // Save id on every change. Effect bodies don't fire on unmount (only cleanup
  // functions do), so fullLogout's clearSavedIds() can't be undone by unmount.
  useEffect(() => {
    savedIds[pageKey] = id;
  }, [id, pageKey]);

  // Pending until the id actually shows up in the URL — not merely until the restore was issued,
  // and never again once it has (`restoreLandedRef`).
  return restoreQueued && !id && !restoreLandedRef.current;
}
